import React, { useEffect, useState, useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import { selectProjectResults } from '../../project-details/data-access/project.reducer';
import PromptInjectionPIRSChart from './PromptInjectionPIRSChart';
import PromptInjectionDriftChart from './PromptInjectionDriftChart';
import PromptInjectionFindingsChart from './PromptInjectionFindingsChart';
import PromptInjectionRiskFactorsChart from './PromptInjectionRiskFactorsChart';
import api from '../../../../app/api/axios-api';

// Define interfaces for LLM Prompt Injection Analysis
interface PromptInjectionFinding {
  Project_ID: string;
  Event_Index: number;
  PIRS: number;
  Risk: string;
  Verdict: string;
  FAISS_Similarity: number;
  Classifier_Probability: number;
  Toxicity_Score: number;
  Obfuscation: {
    base64: boolean;
    hex: boolean;
    zero_width: boolean;
  };
  Obfuscation_Flag: number;
  Breakdown: {
    faiss_sim: number;
    clf_prob: number;
    obf_flag: number;
    tox_flag: number;
    weighted: number;
  };
  Prompt_Preview: string;
  Timestamp: string;
}

interface PromptInjectionSummary {
  Policy_Reference: string;
  Events_Analyzed: number;
  Overall_PIRS: number;
  Overall_Risk: string;
  Overall_Verdict: string;
  Average_FAISS: number;
  Average_Classifier: number;
  Average_Toxicity: number;
  Average_Obfuscation: number;
}

interface PromptInjectionDriftItem {
  Event_Index: number;
  PIRS_Percent: number;
}

interface PromptInjectionAnalysis {
  Summary: PromptInjectionSummary;
  Findings: PromptInjectionFinding[];
  Drift: PromptInjectionDriftItem[];
  Generated_At_UTC: string;
}

interface FindingDataItem {
  Event_Index: number;
  PIRS: number;
  Risk: string;
  Verdict: string;
  FAISS_Similarity: number;
  Classifier_Probability: number;
  Toxicity_Score: number;
  Obfuscation_Flag: number;
  Prompt_Preview: string;
}

interface Props {
  customAnalysis?: Record<string, any>;
}

export const PromptInjectionLLMCard: React.FC<Props> = ({ customAnalysis }) => {
  const projectResults = useSelector(selectProjectResults);
  const [llmPromptInjectionAnalysis, setLlmPromptInjectionAnalysis] = useState<PromptInjectionAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Fetch project data if LLM_PROMPT_INJECTION_ANALYSIS is not in projectResults
  useEffect(() => {
    // If a custom analysis was passed from the 360-analysis API, use it directly
    if (customAnalysis) {
      setLlmPromptInjectionAnalysis(customAnalysis as unknown as PromptInjectionAnalysis);
      setIsLoading(false);
      return;
    }

    const fetchPromptInjectionData = async () => {
      console.log('PromptInjectionLLMCard - projectResults:', projectResults);
      console.log('PromptInjectionLLMCard - collection_results:', projectResults?.collection_results);
      console.log('PromptInjectionLLMCard - prompt_injection:', projectResults?.collection_results?.prompt_injection);

      // Handle array structure: prompt_injection is an array
      let analysisInProjectResults = null;

      // Check results.prompt_injection (array structure) — pick LATEST entry by timestamp
      const resultsPromptInjection = projectResults?.results?.prompt_injection;
      if (Array.isArray(resultsPromptInjection) && resultsPromptInjection.length > 0) {
        const latestPromptInjection = resultsPromptInjection.reduce(
          (latest: any, curr: any) => {
            const latestTs = latest?.timestamp?.$date || latest?.timestamp || '';
            const currTs = curr?.timestamp?.$date || curr?.timestamp || '';
            return currTs >= latestTs ? curr : latest;
          },
          resultsPromptInjection[resultsPromptInjection.length - 1]
        );
        analysisInProjectResults =
          latestPromptInjection?.LLM_PROMPT_INJECTION_ANALYSIS ||
          latestPromptInjection?.llm_prompt_injection_analysis ||
          latestPromptInjection?.LLM_Prompt_Injection_Analysis;
      }

      // Fallback to other paths if not found
      if (!analysisInProjectResults) {
        // Handle collection_results.prompt_injection (might be array)
        const collectionPromptInjection = projectResults?.collection_results?.prompt_injection;
        let analysisInCollectionResults = null;
        if (Array.isArray(collectionPromptInjection) && collectionPromptInjection.length > 0) {
          const latestCollectionPI = collectionPromptInjection[collectionPromptInjection.length - 1];
          analysisInCollectionResults =
            latestCollectionPI?.LLM_PROMPT_INJECTION_ANALYSIS ||
            latestCollectionPI?.llm_prompt_injection_analysis ||
            latestCollectionPI?.LLM_Prompt_Injection_Analysis;
        } else if (collectionPromptInjection && !Array.isArray(collectionPromptInjection)) {
          analysisInCollectionResults =
            collectionPromptInjection?.LLM_PROMPT_INJECTION_ANALYSIS ||
            collectionPromptInjection?.llm_prompt_injection_analysis ||
            collectionPromptInjection?.LLM_Prompt_Injection_Analysis;
        }

        // Handle feature_results.prompt_injection (might be array)
        const featurePromptInjection = projectResults?.feature_results?.prompt_injection;
        let analysisInFeatureResults = null;
        if (Array.isArray(featurePromptInjection) && featurePromptInjection.length > 0) {
          const latestFeaturePI = featurePromptInjection[featurePromptInjection.length - 1];
          analysisInFeatureResults =
            latestFeaturePI?.LLM_PROMPT_INJECTION_ANALYSIS ||
            latestFeaturePI?.llm_prompt_injection_analysis ||
            latestFeaturePI?.LLM_Prompt_Injection_Analysis;
        } else if (featurePromptInjection && !Array.isArray(featurePromptInjection)) {
          analysisInFeatureResults =
            featurePromptInjection?.LLM_PROMPT_INJECTION_ANALYSIS ||
            featurePromptInjection?.llm_prompt_injection_analysis ||
            featurePromptInjection?.LLM_Prompt_Injection_Analysis;
        }

        // Handle legacy object structure in results
        const analysisInResults =
          projectResults?.results?.prompt_injection && !Array.isArray(projectResults.results.prompt_injection)
            ? (projectResults.results.prompt_injection as any)?.LLM_PROMPT_INJECTION_ANALYSIS ||
              (projectResults.results.prompt_injection as any)?.llm_prompt_injection_analysis ||
              (projectResults.results.prompt_injection as any)?.LLM_Prompt_Injection_Analysis
            : null;

        analysisInProjectResults =
          analysisInCollectionResults ||
          analysisInFeatureResults ||
          analysisInResults ||
          (projectResults as any)?.PROMPT_INJECTION_LLM_ANALYSIS ||
          (projectResults as any)?.prompt_injection_llm_analysis ||
          (projectResults as any)?.Prompt_Injection_LLM_Analysis;
      }

      console.log('PromptInjectionLLMCard - analysisInProjectResults:', analysisInProjectResults);

      if (analysisInProjectResults) {
        console.log('PromptInjectionLLMCard - Found analysis in projectResults:', analysisInProjectResults);
        setLlmPromptInjectionAnalysis(analysisInProjectResults);
        setIsLoading(false);
        return;
      }

      // If not found, try to fetch from API
      const projectId =
        projectResults?.projectId ||
        projectResults?.results?.projectId ||
        (projectResults as any)?.project_id ||
        (projectResults as any)?.results?.project_id ||
        localStorage.getItem('projectId') ||
        localStorage.getItem('selectedProjectId') ||
        new URLSearchParams(window.location.search).get('projectId');

      if (!projectId) {
        console.log('PromptInjectionLLMCard - No projectId found');
        setIsLoading(false);
        return;
      }

      try {
        console.log('PromptInjectionLLMCard - Fetching project data for projectId:', projectId);
        const response = await api.get(`/project/get-projects-by-id?projectId=${projectId}`);
        const projectData = response.data?.data || response.data;

        console.log('PromptInjectionLLMCard - API response data:', projectData);
        console.log('PromptInjectionLLMCard - collection_results:', projectData?.collection_results);
        console.log('PromptInjectionLLMCard - prompt_injection:', projectData?.collection_results?.prompt_injection);

        const analysis =
          projectData?.collection_results?.prompt_injection?.LLM_PROMPT_INJECTION_ANALYSIS ||
          projectData?.collection_results?.prompt_injection?.llm_prompt_injection_analysis ||
          projectData?.collection_results?.prompt_injection?.LLM_Prompt_Injection_Analysis ||
          projectData?.PROMPT_INJECTION_LLM_ANALYSIS ||
          projectData?.prompt_injection_llm_analysis ||
          projectData?.Prompt_Injection_LLM_Analysis;

        if (analysis) {
          console.log('PromptInjectionLLMCard - Found analysis in API response:', analysis);
          setLlmPromptInjectionAnalysis(analysis);
        } else {
          console.log('PromptInjectionLLMCard - No PROMPT_INJECTION_LLM_ANALYSIS found in API response');
        }
      } catch (error) {
        console.error('PromptInjectionLLMCard - Error fetching project data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPromptInjectionData();
  }, [projectResults, customAnalysis]);

  // Extract and process prompt injection data
  const extractPromptInjectionData = useMemo(() => {
    const analysisToUse = llmPromptInjectionAnalysis;

    const promptInjectionData: {
      overallPIRS: number;
      overallRisk: string;
      overallVerdict: string;
      driftData: { eventIndex: number; pirsPercent: number }[];
      findingsData: FindingDataItem[];
      averageFAISS: number;
      averageClassifier: number;
      averageObfuscation: number;
      averageToxicity: number;
      eventsAnalyzed: number;
    } = {
      overallPIRS: 0,
      overallRisk: 'Low',
      overallVerdict: 'APPROVE',
      driftData: [],
      findingsData: [],
      averageFAISS: 0,
      averageClassifier: 0,
      averageObfuscation: 0,
      averageToxicity: 0,
      eventsAnalyzed: 0
    };

    if (analysisToUse) {
      // Extract overall PIRS from Summary
      const extractedPIRS = analysisToUse.Summary?.Overall_PIRS;
      promptInjectionData.overallPIRS =
        extractedPIRS !== undefined && extractedPIRS !== null ? Number(extractedPIRS) * 100 : 0; // Convert to percentage

      promptInjectionData.overallRisk = analysisToUse.Summary?.Overall_Risk || 'Low';
      promptInjectionData.overallVerdict = analysisToUse.Summary?.Overall_Verdict || 'APPROVE';

      // Extract average risk factors from Summary
      promptInjectionData.averageFAISS = analysisToUse.Summary?.Average_FAISS ?? 0;
      promptInjectionData.averageClassifier = analysisToUse.Summary?.Average_Classifier ?? 0;
      promptInjectionData.averageObfuscation = analysisToUse.Summary?.Average_Obfuscation ?? 0;
      promptInjectionData.averageToxicity = analysisToUse.Summary?.Average_Toxicity ?? 0;
      promptInjectionData.eventsAnalyzed = analysisToUse.Summary?.Events_Analyzed ?? 0;

      // Use pre-calculated Drift array if available
      if (analysisToUse.Drift && Array.isArray(analysisToUse.Drift)) {
        promptInjectionData.driftData = analysisToUse.Drift.map((driftItem: PromptInjectionDriftItem) => ({
          eventIndex: driftItem.Event_Index,
          pirsPercent: driftItem.PIRS_Percent || 0
        })).sort(
          (a: { eventIndex: number; pirsPercent: number }, b: { eventIndex: number; pirsPercent: number }) =>
            a.eventIndex - b.eventIndex
        );
      } else if (analysisToUse.Findings && Array.isArray(analysisToUse.Findings)) {
        // Fallback: Calculate drift data from Findings
        const eventMap = new Map<number, number[]>();

        analysisToUse.Findings.forEach((finding: PromptInjectionFinding) => {
          const eventIndex = finding.Event_Index;
          const pirsPercent = finding.PIRS * 100; // Convert to percentage

          if (!eventMap.has(eventIndex)) {
            eventMap.set(eventIndex, []);
          }
          eventMap.get(eventIndex)!.push(pirsPercent);
        });

        eventMap.forEach((pirsValues, eventIndex) => {
          const avgPIRS = pirsValues.reduce((sum, val) => sum + val, 0) / pirsValues.length;
          promptInjectionData.driftData.push({
            eventIndex,
            pirsPercent: avgPIRS
          });
        });

        promptInjectionData.driftData.sort(
          (a: { eventIndex: number; pirsPercent: number }, b: { eventIndex: number; pirsPercent: number }) =>
            a.eventIndex - b.eventIndex
        );
      }

      // Process Findings
      if (analysisToUse.Findings && Array.isArray(analysisToUse.Findings)) {
        promptInjectionData.findingsData = analysisToUse.Findings.map((finding: PromptInjectionFinding) => ({
          Event_Index: finding.Event_Index,
          PIRS: finding.PIRS * 100, // Convert to percentage
          Risk: finding.Risk,
          Verdict: finding.Verdict,
          FAISS_Similarity: finding.FAISS_Similarity * 100,
          Classifier_Probability: finding.Classifier_Probability * 100,
          Toxicity_Score: finding.Toxicity_Score * 100,
          Obfuscation_Flag: finding.Obfuscation_Flag,
          Prompt_Preview: finding.Prompt_Preview || ''
        }));
      }
    }

    console.log('PromptInjectionLLMCard - Extracted data:', {
      overallPIRS: promptInjectionData.overallPIRS,
      overallRisk: promptInjectionData.overallRisk,
      overallVerdict: promptInjectionData.overallVerdict,
      driftDataLength: promptInjectionData.driftData.length,
      findingsDataLength: promptInjectionData.findingsData.length
    });

    return promptInjectionData;
  }, [llmPromptInjectionAnalysis]);

  useEffect(() => {
    setIsLoading(false);
  }, [extractPromptInjectionData]);

  // Ensure loading is false once projectResults exists
  useEffect(() => {
    if (projectResults) {
      setIsLoading(false);
    }
  }, [projectResults]);

  if (isLoading && !projectResults) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <CircularProgress />
      </div>
    );
  }

  const hasAnyData =
    extractPromptInjectionData.overallPIRS > 0 ||
    extractPromptInjectionData.driftData.length > 0 ||
    extractPromptInjectionData.findingsData.length > 0 ||
    extractPromptInjectionData.eventsAnalyzed > 0;

  console.log('PromptInjectionLLMCard - hasAnyData:', hasAnyData);
  console.log('PromptInjectionLLMCard - extractPromptInjectionData:', {
    overallPIRS: extractPromptInjectionData.overallPIRS,
    driftDataLength: extractPromptInjectionData.driftData.length,
    findingsDataLength: extractPromptInjectionData.findingsData.length,
    eventsAnalyzed: extractPromptInjectionData.eventsAnalyzed,
    llmPromptInjectionAnalysis: llmPromptInjectionAnalysis
  });

  if (!hasAnyData) {
    return (
      <div className="text-center py-5">
        <p className="text-muted">No prompt injection analysis data available. Please run the analysis first.</p>
      </div>
    );
  }

  return (
    <Row className="g-3 mt-4">
      {/* Overall PIRS Chart */}
      {extractPromptInjectionData.overallPIRS > 0 && (
        <Col xs={12}>
          <PromptInjectionPIRSChart
            overallPIRS={extractPromptInjectionData.overallPIRS}
            overallRisk={extractPromptInjectionData.overallRisk}
            overallVerdict={extractPromptInjectionData.overallVerdict}
            title={`Overall PIRS: ${extractPromptInjectionData.overallPIRS.toFixed(2)}% - ${
              extractPromptInjectionData.overallRisk
            } Risk`}
            description="Overall Prompt Injection Risk Score (PIRS) across all events"
          />
        </Col>
      )}

      {/* Average Risk Factors Chart */}
      {extractPromptInjectionData.eventsAnalyzed > 0 && (
        <Col xs={12}>
          <PromptInjectionRiskFactorsChart
            averageFAISS={extractPromptInjectionData.averageFAISS}
            averageClassifier={extractPromptInjectionData.averageClassifier}
            averageObfuscation={extractPromptInjectionData.averageObfuscation}
            averageToxicity={extractPromptInjectionData.averageToxicity}
            overallPIRS={extractPromptInjectionData.overallPIRS / 100}
            eventsAnalyzed={extractPromptInjectionData.eventsAnalyzed}
            title={`Average Prompt Injection Risk Factors Across ${extractPromptInjectionData.eventsAnalyzed} LLM Events`}
            description="Average values for each risk factor component across all analyzed events"
          />
        </Col>
      )}

      {/* PIRS Drift Across Events */}
      {extractPromptInjectionData.driftData.length > 0 && (
        <Col xs={12}>
          <PromptInjectionDriftChart
            data={extractPromptInjectionData.driftData}
            title="PIRS Drift Across Events"
            description="Prompt Injection Risk Score variation across different events"
          />
        </Col>
      )}

      {/* Findings Breakdown */}
      {extractPromptInjectionData.findingsData.length > 0 && (
        <Col xs={12}>
          <PromptInjectionFindingsChart
            data={extractPromptInjectionData.findingsData}
            title="Prompt Injection Findings by Event"
            description="Detailed breakdown of PIRS components for each event"
          />
        </Col>
      )}
    </Row>
  );
};
